import React, { useState, useCallback } from "react"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
} from "@mui/material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"
import VideocamIcon from "@mui/icons-material/Videocam"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import useCameraStream from "../../hooks/useCameraStream"

const CameraStream = () => {
  const [overlayImage, setOverlayImage] = useState(null)
  const [opacity, setOpacity] = useState(0.5)

  const fetchClimb = useCallback(async (file, { setLoading, loadingRef }) => {
    setLoading(true)
    loadingRef.current = true
    const formData = new FormData()
    formData.append("image", file)
    formData.append("streaming", true)

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/find_climb`,
        {
          method: "POST",
          body: formData,
        }
      )
      const json = await response.json()
      const encodedImage = `data:image/png;base64,${json.query_image_hold_locations}`
      setOverlayImage(encodedImage)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
      loadingRef.current = false
    }
  }, [])

  const {
    videoRef,
    canvasRef,
    devices,
    selectedDeviceId,
    handleDeviceChange,
    isCapturing,
    toggleCapture,
  } = useCameraStream({ onFile: fetchClimb })

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Controls bar */}
      <Card sx={{ width: "100%", maxWidth: 640 }}>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            "&:last-child": { pb: 2 },
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Camera</InputLabel>
            <Select
              value={selectedDeviceId}
              onChange={handleDeviceChange}
              label="Camera"
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant={isCapturing ? "outlined" : "contained"}
            onClick={toggleCapture}
            startIcon={isCapturing ? <StopIcon /> : <PlayArrowIcon />}
            color={isCapturing ? "error" : "primary"}
            sx={{ minWidth: 120 }}
          >
            {isCapturing ? "Stop" : "Start"}
          </Button>

          {isCapturing && (
            <FiberManualRecordIcon
              sx={(theme) => ({
                color: theme.palette.error.main,
                fontSize: 14,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              })}
            />
          )}
        </CardContent>
      </Card>

      {/* Video feed with overlay */}
      <Card sx={{ width: "100%", maxWidth: 640, overflow: "hidden" }}>
        <Box sx={{ position: "relative", lineHeight: 0 }}>
          {/* Live video feed — always visible as the base layer */}
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              width: "100%",
              display: "block",
              bgcolor: "background.default",
              // Reserve space before video loads
              minHeight: 200,
            }}
          />

          {/* Detection overlay on top of the video */}
          {overlayImage && (
            <Box
              component="img"
              src={overlayImage}
              alt="Detection overlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "fill",
                opacity: opacity,
                pointerEvents: "none",
              }}
            />
          )}

          {/* Empty state hint when video hasn't started */}
          {!isCapturing && !overlayImage && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                color: "text.secondary",
                bgcolor: "rgba(0,0,0,0.5)",
              }}
            >
              <VideocamIcon sx={{ fontSize: 56, opacity: 0.5 }} />
              <Typography variant="body1">
                Select a camera and press Start
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Live climb detection will appear here
              </Typography>
            </Box>
          )}
        </Box>

        {/* Opacity slider — only show when there's an overlay */}
        {overlayImage && (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: "0.8rem" }}
            >
              Overlay opacity
            </Typography>
            <Slider
              value={opacity}
              onChange={(e, v) => setOpacity(v)}
              min={0}
              max={1}
              step={0.01}
              size="small"
            />
          </Box>
        )}
      </Card>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </Box>
  )
}

export default CameraStream
